import React, { useEffect } from 'react';
import { useContent } from '../hooks/useContent';
import { resolveAssetUrl } from '../lib/api';

const useReveal = () => {
  useEffect(() => {
    const els = document.querySelectorAll('.reveal');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); });
    }, { threshold: 0.12 });
    els.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);
};

const DEFAULT_FOUNDERS_PHOTO = 'https://customer-assets-rejwkqb3.emergentagent.net/job_stone-temple-preview/artifacts/5lxluika_l6wi64cu_i1.png';

const Home = () => {
  useReveal();
  const { t } = useContent('home');
  const photoUrl = resolveAssetUrl(t('founders_photo.url', '') || DEFAULT_FOUNDERS_PHOTO);
  return (
    <main className="pt-28" data-testid="home-page">
      {/* Hero */}
      <section className="relative min-h-[80vh] flex items-center overflow-hidden" data-testid="home-hero">
        <div className="max-w-[1440px] mx-auto px-8 md:px-14 w-full text-center flex flex-col items-center">
          <p className="font-sans text-[11px] tracking-[0.35em] text-navy/60 mb-8 animate-fade-in">
            {t('hero.kicker', 'STONE TEMPLE PARTNERS · EST. 2020')}
          </p>
          <h1
            className="font-serif text-[52px] md:text-[80px] lg:text-[104px] leading-[0.95] text-navy tracking-tight animate-fade-in"
            data-testid="home-hero-heading"
          >
            {t('hero.title_line1', 'Guiding')}<br />
            <span className="italic">{t('hero.title_line2', 'Generational Wealth')}</span>
          </h1>
          <p className="font-serif text-xl md:text-2xl text-navy/85 leading-relaxed mt-10 max-w-3xl">
            {t('hero.body', 'When your wealth reaches the point where you are moving on to the bigger questions in life, you have outgrown traditional wealth management. Stone Temple Partners combines deep institutional expertise with personalized guidance — partnering with you to guide your generational wealth so you can focus on what matters most.')}
          </p>
        </div>
      </section>

      {/* Founders horizontal photo directly under intro */}
      <section className="pt-8 pb-16 md:pt-12 md:pb-24" data-testid="home-founders-photo">
        <div className="max-w-[1440px] mx-auto px-8 md:px-14">
          <div className="relative w-full overflow-hidden bg-cream2 reveal">
            <img src={photoUrl} alt="Stone Temple Partners founders" className="block w-full h-auto" />
            <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(to top, rgba(23,50,72,0.35) 0%, rgba(23,50,72,0) 40%)' }} />
            <div className="absolute bottom-6 md:bottom-8 left-6 md:left-10 max-w-md">
              <p className="font-sans text-[10px] tracking-[0.35em] text-cream/85 mb-2">{t('founders_photo.caption_kicker', 'THE FOUNDERS')}</p>
              <p className="font-serif italic text-2xl md:text-3xl text-cream leading-tight">
                {t('founders_photo.caption', 'Independent by design, aligned by choice.')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Are · Who We Serve · How We Serve */}
      <section className="py-16 md:py-24 border-t border-navy/10" data-testid="home-static-pillars">
        <div className="max-w-[1440px] mx-auto px-8 md:px-14">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12">
            <StaticBlock
              label={t('pillar1.label', 'Who We Are')}
              subtitle={t('pillar1.subtitle', 'Built by practitioners')}
              desc={t('pillar1.body', "A team that has spent nearly a decade running dozens of sophisticated family offices — including our own. We don't bring a solution to clients until we understand their near-term objectives, long-term goals, and their values.")}
            />
            <StaticBlock
              label={t('pillar2.label', 'Who We Serve')}
              subtitle={t('pillar2.subtitle', 'Families, advisors, and institutions')}
              desc={t('pillar2.body', 'Full Spectrum Advisory for private clients, RIA consulting, and purpose-built investment solutions — three coordinated ways to work with the families and advisors we serve.')}
            />
            <StaticBlock
              label={t('pillar3.label', 'How We Serve')}
              subtitle={t('pillar3.subtitle', 'Every dollar has a purpose')}
              desc={t('pillar3.body', 'Most plans never ask what it is. Full Spectrum Advisory treats every financial decision as part of one coherent strategy — organized around six interconnected domains of wealth and expressed through purpose-built portfolios.')}
            />
          </div>
        </div>
      </section>

      {/* Our Commitment */}
      <section className="py-16 md:py-24 border-t border-navy/10" data-testid="home-commitment">
        <div className="max-w-[1440px] mx-auto px-8 md:px-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5 reveal">
              <p className="font-sans text-[11px] tracking-[0.35em] text-navy/60 mb-6">{t('commitment.kicker', 'OUR COMMITMENT')}</p>
              <h2 className="font-serif text-3xl md:text-4xl text-navy leading-snug" data-testid="home-commitment-subtitle">
                {t('commitment.subtitle', 'Partnership that endures across generations.')}
              </h2>
            </div>
            <div className="lg:col-span-7 lg:pt-4 reveal">
              <p className="font-sans text-base md:text-lg text-navy/75 leading-relaxed max-w-2xl" data-testid="home-commitment-body">
                {t('commitment.body', 'Stone Temple Partners was built by practitioners for the families we serve. We invest alongside you, align our incentives to yours, and bring the same institutional discipline to your balance sheet that we bring to our own — so every decision earns the right to be part of your long-term plan.')}
              </p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

const StaticBlock = ({ label, subtitle, desc }) => (
  <div className="border-t border-navy/20 pt-8 reveal" data-testid={`home-block-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <h3 className="font-serif text-3xl md:text-4xl text-navy leading-tight">{label}</h3>
    <p className="font-serif italic text-lg text-navy/60 mt-3">{subtitle}</p>
    <p className="font-sans text-sm text-navy/75 mt-6 leading-relaxed">{desc}</p>
  </div>
);

export default Home;
